import Box, { type BoxProps } from '@liquidweb-dev/undercurrent/Box';
import AcronisIcon from '@liquidweb-dev/undercurrent/icons/platform/Acronis';
import Imunify360Icon from '@liquidweb-dev/undercurrent/icons/platform/Imunify360';
import ThreatDownIcon from '@liquidweb-dev/undercurrent/icons/platform/ThreatDown';
import GlobeLockIcon from '@liquidweb-dev/undercurrent/icons/lucide/GlobeLock';
import GlobeIcon from '@liquidweb-dev/undercurrent/icons/lucide/Globe';
import CloudIcon from '@liquidweb-dev/undercurrent/icons/lucide/Cloud';
import BrickWallFireIcon from '@liquidweb-dev/undercurrent/icons/lucide/BrickWallFire';
import ServerCogIcon from '@liquidweb-dev/undercurrent/icons/lucide/ServerCog';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import { type Theme } from '@liquidweb-dev/undercurrent';
import ShieldCheckIcon from '@liquidweb-dev/undercurrent/icons/lucide/ShieldCheck';
import BoxIcon from '@liquidweb-dev/undercurrent/icons/lucide/Box';

const GRADIENT_ACRONIS =
	'linear-gradient(0deg, #00204D 0%, #00204D 100%), linear-gradient(81deg, #00B600 0%, #003F99 100%)';
const GRADIENT_DDOS =
	'linear-gradient(0deg, #001738 0%, #001738 100%), linear-gradient(81deg, #00B600 0%, #003F99 100%)';
const GRADIENT_IMUNIFY360 =
	'linear-gradient(0deg, #007D3F 0%, #007D3F 100%), linear-gradient(81deg, #00B600 0%, #003F99 100%)';
const GRADIENT_THREATDOWN =
	'linear-gradient(0deg, #2B3A67 0%, #2B3A67 100%), linear-gradient(81deg, #00B600 0%, #003F99 100%)';

const getIconSize = (theme: Theme, size?: 'small' | 'medium') => {
	if (size === 'medium') {
		return { height: theme.size.md, width: theme.size.md };
	}

	return { height: theme.size.xS, width: theme.size.xS };
};

const Wrapper = ({
	size,
	sx,
	...rest
}: BoxProps & { size?: 'small' | 'medium' }) => {
	return (
		<Box
			sx={combineSx(
				({ borderRadius, vars }) => ({
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					borderRadius:
						size === 'medium' ? borderRadius.rounded : borderRadius.roundedXS,
					padding: size === 'medium' ? 200 : 100,
					color: vars.palette.uc.icon.strong,
				}),
				sx,
			)}
			{...rest}
		/>
	);
};

export interface AddOnIconProps extends BoxProps {
	type:
		| 'Acronis'
		| 'Cloud Backup'
		| 'Cloud.VPN'
		| 'DDoS.Server.Protect'
		| 'Firewall'
		| 'Networking.Private.VPN'
		| 'ServerSecurity.Imunify360'
		| 'ServerSecurity.ThreatDown'
		| 'SS.ObjectStore'
		| 'SS.IP'
		| 'SS.VIP'
		| 'Cloud.LoadBalancer'
		| 'SS.ObjectStore';
	size?: 'small' | 'medium';
}

const AddOnIcon = ({ type, size, sx, ...rest }: AddOnIconProps) => {
	if (type === 'Acronis') {
		return (
			<Wrapper
				sx={combineSx(
					{
						background: GRADIENT_ACRONIS,
					},
					sx,
				)}
				size={size}
				{...rest}
			>
				<AcronisIcon sx={(theme) => getIconSize(theme, size)} />
			</Wrapper>
		);
	}

	if (type === 'DDoS.Server.Protect') {
		return (
			<Wrapper
				sx={combineSx(
					{
						background: GRADIENT_DDOS,
					},
					sx,
				)}
				size={size}
				{...rest}
			>
				<ShieldCheckIcon sx={(theme) => getIconSize(theme, size)} />
			</Wrapper>
		);
	}

	if (type === 'ServerSecurity.Imunify360') {
		return (
			<Wrapper
				sx={combineSx(
					{
						background: GRADIENT_IMUNIFY360,
					},
					sx,
				)}
				size={size}
				{...rest}
			>
				<Imunify360Icon monochrome sx={(theme) => getIconSize(theme, size)} />
			</Wrapper>
		);
	}

	if (type === 'ServerSecurity.ThreatDown') {
		return (
			<Wrapper
				sx={combineSx(
					{
						background: GRADIENT_THREATDOWN,
					},
					sx,
				)}
				size={size}
				{...rest}
			>
				<ThreatDownIcon monochrome sx={(theme) => getIconSize(theme, size)} />
			</Wrapper>
		);
	}

	if (type === 'Cloud.VPN') {
		return (
			<Wrapper
				sx={combineSx(
					({ vars }) => ({
						background: vars.palette.uc.bg.strong,
					}),
					sx,
				)}
				size={size}
				{...rest}
			>
				<GlobeLockIcon sx={(theme) => getIconSize(theme, size)} />
			</Wrapper>
		);
	}

	if (type === 'Cloud Backup') {
		return (
			<Wrapper
				sx={combineSx(
					({ vars }) => ({
						background: vars.palette.uc.bg.strong,
					}),
					sx,
				)}
				size={size}
				{...rest}
			>
				<CloudIcon
					sx={(theme) => ({
						...getIconSize(theme, size),
					})}
				/>
			</Wrapper>
		);
	}

	if (type === 'Firewall') {
		return (
			<Wrapper
				sx={combineSx(
					({ vars }) => ({
						background: vars.palette.uc.highlight.bg.main,
						border: `1px solid ${vars.palette.uc.highlight.border.weak}`,
					}),
					sx,
				)}
				size={size}
				{...rest}
			>
				<BrickWallFireIcon
					sx={(theme) => ({
						color: theme.vars.palette.uc.highlight.icon.main,
						...getIconSize(theme, size),
					})}
				/>
			</Wrapper>
		);
	}

	if (type === 'Networking.Private.VPN') {
		return (
			<Wrapper
				sx={combineSx(
					({ vars }) => ({
						border: '1px solid',
						borderColor: vars.palette.uc.highlight.border.weak,
						background: vars.palette.uc.highlight.bg.main,
					}),
					sx,
				)}
				size={size}
				{...rest}
			>
				<GlobeLockIcon
					sx={(theme) => ({
						color: theme.vars.palette.uc.highlight.icon.main,
						...getIconSize(theme, size),
					})}
				/>
			</Wrapper>
		);
	}

	if (type === 'Cloud.LoadBalancer') {
		return (
			<Wrapper
				sx={combineSx(
					(theme) => ({
						background: theme.vars.palette.uc.bg.strong,
					}),
					sx,
				)}
				size={size}
				{...rest}
			>
				<ServerCogIcon sx={(theme) => getIconSize(theme, size)} />
			</Wrapper>
		);
	}
	if (type === 'SS.ObjectStore') {
		return (
			<Wrapper
				sx={combineSx(
					(theme) => ({
						background: theme.vars.palette.uc.bg.strong,
					}),
					sx,
				)}
				size={size}
				{...rest}
			>
				<BoxIcon sx={(theme) => getIconSize(theme, size)} />
			</Wrapper>
		);
	}

	return (
		<Wrapper
			sx={combineSx(
				(theme) => ({
					background: theme.vars.palette.uc.bg.strong,
				}),
				sx,
			)}
			size={size}
			{...rest}
		>
			<GlobeIcon sx={(theme) => getIconSize(theme, size)} />
		</Wrapper>
	);
};

export default AddOnIcon;
